export type YearKey = 'reception' | 'year1' | 'year2' | 'year3' | 'year4' | 'year5_6';

export const YEAR_KEYS: readonly YearKey[] = [
  'reception',
  'year1',
  'year2',
  'year3',
  'year4',
  'year5_6'
];

export interface AdditionSpec {
  /** Build every ordered pair `a + b` for a, b in [minOperand, maxOperand]. */
  readonly minOperand?: number;
  readonly maxOperand?: number;
  /** Or use this explicit fact set verbatim (each pair is `operand1 + operand2`). */
  readonly facts?: ReadonlyArray<readonly [number, number]>;
}

export interface MultiplicationSpec {
  /** Build every `multiplier x table` for each table and multiplier in [minMultiplier, maxMultiplier]. */
  readonly tables?: readonly number[];
  readonly minMultiplier?: number;
  readonly maxMultiplier?: number;
  /** Or use this explicit fact set verbatim (each pair is `operand1 x operand2`). */
  readonly facts?: ReadonlyArray<readonly [number, number]>;
}

export interface YearConfig {
  readonly key: YearKey;
  readonly label: string;
  readonly shortLabel: string;
  readonly description: string;
  readonly timeLimitSeconds: number;
  readonly questionCount: number;
  readonly additionCount: number;
  readonly multiplicationCount: number;
  readonly addition: AdditionSpec | null;
  readonly multiplication: MultiplicationSpec | null;
  readonly mixAdjustable: boolean;
}

/** Every unordered single-digit pair `first <= second` within [min, max]. */
function unorderedFacts(min: number, max: number): ReadonlyArray<readonly [number, number]> {
  const facts: Array<readonly [number, number]> = [];
  for (let first = min; first <= max; first += 1) {
    for (let second = first; second <= max; second += 1) {
      facts.push([first, second]);
    }
  }
  return facts;
}

// The canonical 36 multiplication "Learn Its" facts: every digit pair from 2 to 9.
const CORE_MULTIPLICATION_FACTS = unorderedFacts(2, 9);

export const YEAR_CONFIGS: Record<YearKey, YearConfig> = {
  reception: {
    key: 'reception',
    label: 'Reception',
    shortLabel: 'Rec',
    description: 'Adding within 5',
    timeLimitSeconds: 20,
    questionCount: 7,
    additionCount: 7,
    multiplicationCount: 0,
    addition: { minOperand: 1, maxOperand: 5 },
    multiplication: null,
    mixAdjustable: false
  },
  year1: {
    key: 'year1',
    label: 'Year 1',
    shortLabel: 'Y1',
    description: 'Addition facts',
    timeLimitSeconds: 30,
    questionCount: 17,
    additionCount: 17,
    multiplicationCount: 0,
    addition: { minOperand: 1, maxOperand: 9 },
    multiplication: null,
    mixAdjustable: false
  },
  year2: {
    key: 'year2',
    label: 'Year 2',
    shortLabel: 'Y2',
    description: 'Addition and the 2, 5 and 10 times tables',
    timeLimitSeconds: 90,
    questionCount: 40,
    additionCount: 16,
    multiplicationCount: 24,
    addition: { minOperand: 3, maxOperand: 9 },
    multiplication: { tables: [2, 5, 10], minMultiplier: 2, maxMultiplier: 9 },
    mixAdjustable: true
  },
  year3: {
    key: 'year3',
    label: 'Year 3',
    shortLabel: 'Y3',
    description: 'The 3, 4 and 9 times tables',
    timeLimitSeconds: 60,
    questionCount: 24,
    additionCount: 0,
    multiplicationCount: 24,
    addition: null,
    multiplication: { tables: [3, 4, 9], minMultiplier: 2, maxMultiplier: 9 },
    mixAdjustable: false
  },
  year4: {
    key: 'year4',
    label: 'Year 4',
    shortLabel: 'Y4',
    description: '36 addition facts plus the six 6, 7 and 8 times-table facts',
    timeLimitSeconds: 60,
    questionCount: 42,
    additionCount: 36,
    multiplicationCount: 6,
    addition: { minOperand: 2, maxOperand: 9 },
    multiplication: { facts: [[6, 6], [6, 7], [6, 8], [7, 7], [7, 8], [8, 8]] },
    mixAdjustable: true
  },
  year5_6: {
    key: 'year5_6',
    label: 'Year 5 & 6',
    shortLabel: 'Y5/6',
    description: 'All 36 addition facts and all 36 multiplication facts',
    timeLimitSeconds: 100,
    questionCount: 72,
    additionCount: 36,
    multiplicationCount: 36,
    addition: { minOperand: 2, maxOperand: 9 },
    multiplication: { facts: CORE_MULTIPLICATION_FACTS },
    mixAdjustable: true
  }
};

export function listYearConfigs(): YearConfig[] {
  return YEAR_KEYS.map((key) => YEAR_CONFIGS[key]);
}

export function isYearKey(value: unknown): value is YearKey {
  return typeof value === 'string' && (YEAR_KEYS as readonly string[]).includes(value);
}

export function getYearConfig(key: YearKey): YearConfig {
  const config = YEAR_CONFIGS[key];
  if (!config) {
    throw new Error(`Unknown year key: ${String(key)}`);
  }
  return config;
}
